/**
 * Dashboards Tab - CRUD interface for dashboards with grid layout.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import * as api from '../../api/analytics';
import { renderDashboardPanels, showLoading } from '../../lib/vizRunner';

export default function DashboardsTab() {
  const [dashboards, setDashboards] = useState([]);
  const [visualizations, setVisualizations] = useState([]);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showViewer, setShowViewer] = useState(false);

  // Editor state
  const [editorName, setEditorName] = useState('');
  const [editorDescription, setEditorDescription] = useState('');
  const [editorPanels, setEditorPanels] = useState([]);
  const [editorError, setEditorError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Viewer state
  const [viewerData, setViewerData] = useState(null);
  const [isRendering, setIsRendering] = useState(false);
  const panelRefs = useRef({});

  // Load data
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [dashResponse, vizResponse] = await Promise.all([
        api.fetchDashboards(),
        api.fetchVisualizations(),
      ]);
      setDashboards(dashResponse?.dashboards || []);
      setVisualizations(vizResponse?.visualizations || []);
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Open editor for new dashboard
  const handleNewDashboard = () => {
    setSelectedDashboard(null);
    setEditorName('');
    setEditorDescription('');
    setEditorPanels([]);
    setEditorError(null);
    setShowEditor(true);
    setShowViewer(false);
  };

  // Open editor for existing dashboard
  const handleEditDashboard = async (dashId) => {
    try {
      const response = await api.fetchDashboard(dashId);
      const dash = response;
      setSelectedDashboard(dash);
      setEditorName(dash.name);
      setEditorDescription(dash.description || '');

      // Parse config to get panels
      const config = dash.config || {};
      setEditorPanels(config.panels || []);
      setEditorError(null);
      setShowEditor(true);
      setShowViewer(false);
    } catch (err) {
      setError(err.message || 'Failed to load dashboard');
    }
  };

  // View dashboard
  const handleViewDashboard = async (dashId) => {
    setIsRendering(true);
    setShowViewer(true);
    setShowEditor(false);
    setViewerData(null);

    try {
      // First get the dashboard config
      const dashResponse = await api.fetchDashboard(dashId);
      setSelectedDashboard(dashResponse);

      // Then render it
      const renderResponse = await api.renderDashboard(dashId, {});
      setViewerData(renderResponse);
    } catch (err) {
      setError(err.message || 'Failed to render dashboard');
    } finally {
      setIsRendering(false);
    }
  };

  // Render panels when viewer data changes
  useEffect(() => {
    if (!viewerData || !showViewer) return;

    const panels = viewerData.panels || [];
    const renderPanels = panels.map((panelResult) => ({
      element: panelRefs.current[panelResult.panel_id],
      panelResult,
    }));

    renderDashboardPanels(renderPanels);
  }, [viewerData, showViewer]);

  // Add panel
  const handleAddPanel = () => {
    if (visualizations.length === 0) {
      setEditorError('Create a visualization first to add to the dashboard');
      return;
    }

    const newPanel = {
      id: `panel_${Date.now()}`,
      visualization_id: visualizations[0].id,
      position: { x: 0, y: editorPanels.length * 4, w: 6, h: 4 },
      filter_mapping: {},
      static_params: {},
    };

    setEditorPanels([...editorPanels, newPanel]);
  };

  // Update panel
  const handleUpdatePanel = (panelId, updates) => {
    setEditorPanels((prev) =>
      prev.map((p) => (p.id === panelId ? { ...p, ...updates } : p))
    );
  };

  // Remove panel
  const handleRemovePanel = (panelId) => {
    setEditorPanels((prev) => prev.filter((p) => p.id !== panelId));
  };

  // Save dashboard
  const handleSave = async () => {
    if (!editorName.trim()) {
      setEditorError('Name is required');
      return;
    }

    setIsSaving(true);
    setEditorError(null);

    const config = {
      global_filters: [],
      panels: editorPanels,
    };

    try {
      if (selectedDashboard) {
        await api.updateDashboard(selectedDashboard.id, {
          name: editorName,
          description: editorDescription,
          config,
        });
      } else {
        await api.createDashboard({
          name: editorName,
          description: editorDescription,
          config,
        });
      }

      await loadData();
      setShowEditor(false);
    } catch (err) {
      setEditorError(err.response?.data?.message || err.message || 'Failed to save dashboard');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete dashboard
  const handleDelete = async (dashId) => {
    if (!window.confirm('Are you sure you want to delete this dashboard?')) return;

    try {
      await api.deleteDashboard(dashId);
      await loadData();
      if (selectedDashboard?.id === dashId) {
        setShowEditor(false);
        setShowViewer(false);
        setSelectedDashboard(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete dashboard');
    }
  };

  // Get visualization name by ID
  const getVizName = (vizId) => {
    const viz = visualizations.find((v) => v.id === vizId);
    return viz?.name || 'Unknown';
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">Dashboards</h2>
        <button
          onClick={handleNewDashboard}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
        >
          + New Dashboard
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
          <button onClick={() => setError(null)} className="ml-2 text-red-500">&times;</button>
        </div>
      )}

      {showViewer ? (
        /* Dashboard Viewer */
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-medium text-gray-900">
              {selectedDashboard?.name}
            </h3>
            <button
              onClick={() => { setShowViewer(false); setSelectedDashboard(null); }}
              className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Back to List
            </button>
          </div>

          {isRendering ? (
            <div className="p-8 text-center text-gray-500">Rendering dashboard...</div>
          ) : viewerData?.panels?.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              This dashboard has no panels. Edit it to add visualizations.
            </div>
          ) : (
            <div className="grid grid-cols-12 gap-4">
              {viewerData?.panels?.map((panelResult) => {
                const config = selectedDashboard?.config || {};
                const panelConfig = config.panels?.find((p) => p.id === panelResult.panel_id);
                const position = panelConfig?.position || { w: 6, h: 4 };

                return (
                  <div
                    key={panelResult.panel_id}
                    className="bg-white rounded-lg shadow overflow-hidden"
                    style={{
                      gridColumn: `span ${position.w}`,
                      minHeight: `${position.h * 60}px`,
                    }}
                  >
                    <div className="p-2 border-b bg-gray-50 text-sm font-medium text-gray-700">
                      {getVizName(panelConfig?.visualization_id)}
                      {!panelResult.success && (
                        <span className="ml-2 text-red-500">Error</span>
                      )}
                    </div>
                    <div
                      ref={(el) => { panelRefs.current[panelResult.panel_id] = el; }}
                      className="h-64"
                    />
                  </div>
                );
              })}
            </div>
          )}

          {viewerData && (
            <div className="text-sm text-gray-500">
              Rendered in {viewerData.total_ms}ms
              {viewerData.failed_count > 0 && (
                <span className="text-red-500 ml-2">
                  ({viewerData.failed_count} panel(s) failed)
                </span>
              )}
            </div>
          )}
        </div>
      ) : showEditor ? (
        /* Dashboard Editor */
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <div className="flex justify-between items-center border-b pb-4">
            <h3 className="text-lg font-medium">
              {selectedDashboard ? 'Edit Dashboard' : 'New Dashboard'}
            </h3>
            <button
              onClick={() => setShowEditor(false)}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              &times;
            </button>
          </div>

          {/* Name & Description */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                type="text"
                value={editorName}
                onChange={(e) => setEditorName(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="My Dashboard"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <input
                type="text"
                value={editorDescription}
                onChange={(e) => setEditorDescription(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="What does this dashboard show?"
              />
            </div>
          </div>

          {/* Panels */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-700">Panels</label>
              <button
                onClick={handleAddPanel}
                className="text-sm text-primary-600 hover:text-primary-800"
              >
                + Add Panel
              </button>
            </div>

            {editorPanels.length === 0 ? (
              <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500">
                No panels yet. Add a panel to include visualizations.
              </div>
            ) : (
              <div className="space-y-3">
                {editorPanels.map((panel, idx) => (
                  <div
                    key={panel.id}
                    className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg"
                  >
                    <span className="text-sm text-gray-500">#{idx + 1}</span>

                    <select
                      value={panel.visualization_id}
                      onChange={(e) =>
                        handleUpdatePanel(panel.id, { visualization_id: e.target.value })
                      }
                      className="flex-1 px-3 py-2 border rounded-lg text-sm"
                    >
                      {visualizations.map((viz) => (
                        <option key={viz.id} value={viz.id}>
                          {viz.name}
                        </option>
                      ))}
                    </select>

                    <div className="flex items-center gap-2 text-sm">
                      <label className="text-gray-500">Width:</label>
                      <select
                        value={panel.position?.w || 6}
                        onChange={(e) =>
                          handleUpdatePanel(panel.id, {
                            position: { ...panel.position, w: parseInt(e.target.value) },
                          })
                        }
                        className="px-2 py-1 border rounded"
                      >
                        <option value={3}>3 cols</option>
                        <option value={4}>4 cols</option>
                        <option value={6}>6 cols</option>
                        <option value={8}>8 cols</option>
                        <option value={12}>Full</option>
                      </select>
                    </div>

                    <button
                      onClick={() => handleRemovePanel(panel.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Error */}
          {editorError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {editorError}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={() => setShowEditor(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        /* Dashboards List */
        <div className="bg-white rounded-lg shadow">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading dashboards...</div>
          ) : dashboards.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p className="mb-4">No dashboards yet. Create your first dashboard.</p>
              <button
                onClick={handleNewDashboard}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Create Dashboard
              </button>
            </div>
          ) : (
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Name</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Panels</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Source</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {dashboards.map((dash) => (
                  <tr key={dash.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{dash.name}</div>
                      {dash.description && (
                        <div className="text-sm text-gray-500">{dash.description}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {dash.visualization_count || 0} visualizations
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                        dash.source === 'reference'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {dash.source}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewDashboard(dash.id)}
                          className="text-green-600 hover:text-green-800"
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleEditDashboard(dash.id)}
                          className="text-primary-600 hover:text-primary-800"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(dash.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
